import { routes } from "@/routes/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  PROPERTY_ID,
  OCP_ID,
  OWNER,
  steps,
  RECEIPT_PROOF,
  CHEQUE_NO,
  RECEIPT_ID,
  RECEIPTS,
  PROOF_FILE,
  ATTACHMENT_ID,
  OWNER_ID,
} from "./consts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useAddOCPFeeDocMutation, useAddOCPIntakeDocumentMutation, useAddOwnershipChangePacketMutation, useGetOwnershipChangePacketById, useGetPropertiesByCommunity, useGetUserPendingBillCount } from "@/services";
import { toast } from "@/components/Toast";
import { setOCPData, setPendingBillData } from "@/redux/slices";
import { useQueryClient } from "@tanstack/react-query";

const useAddEditOCP = () => {
  const { id, action } = useParams();
  const isView = !!id && action == "view";
  const isEdit = !!id && action == "edit";
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const ocpData = useSelector(
    (state: RootState) => state.currentData.ocpData
  );
  const pendingBillData = useSelector(
    (state: RootState) => state.currentData.pendingBillData
  );
  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const [activeStep, setActiveStep] = useState(1);
  const proofDocs = useRef<any>(null);
  const isFeeDraft = useRef<boolean>(false);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.admin}/${routes.masterData}/${routes.ownerChangePackage}`,
        name: `Ownership Change Packet - ${communityData?.communityName || communityData?.name}`,
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            {isView ? "View" : isEdit ? "Edit" : "Create New"}
          </span>
        ),
      },
    ],
    [isEdit, isView]
  );

  const { data: userPendingBillCount, refetch: refetchUserPendingBillCount } =
    useGetUserPendingBillCount({
      communityId: communityData?.communityId,
      userId: pendingBillData?.[OWNER_ID] || "",
      propertyId: pendingBillData?.[PROPERTY_ID] || "",
      queryParams: {
        enabled: false,
      },
    });

    const propertyId = pendingBillData?.[PROPERTY_ID];
    const ownerId = pendingBillData?.[OWNER_ID];
  
    // Safely trigger refetch only when IDs change and exist
    useEffect(() => {
      if (propertyId && ownerId) {
        refetchUserPendingBillCount();
      }
    }, [propertyId, ownerId]);

  const isBillPending = useMemo(() => {
    dispatch(setPendingBillData({...pendingBillData, isPending: userPendingBillCount?.data?.pendingBillCount > 0 && pendingBillData?.[PROPERTY_ID]}))
    return userPendingBillCount?.data?.pendingBillCount > 0 && pendingBillData?.[PROPERTY_ID]
  }, [userPendingBillCount]);

  const title = useMemo(() => {
    const action = isView ? "View " : isEdit ? "Edit" : "Create New";
    return `${action} Ownership Change Packet`
  }, [isEdit, isView]);

  const onStepperClick = (currentStep: number) => {
    if (isView) setActiveStep(currentStep);
    if (isEdit) {
      const findStepIndex = steps?.find((data: any) => data.status == ocpData.status)?.id || currentStep;

      if (currentStep <= findStepIndex || (findStepIndex !== 2 && currentStep === findStepIndex + 1) || currentStep == findStepIndex) {
        setActiveStep(currentStep);
      }
    }
  };

  const onCancel = () => {
    navigate(`/${routes.admin}/${routes.masterData}/${routes.ownerChangePackage}`);
  };

  const onNextStep = () => {
    setActiveStep((prev: number) => prev + 1);
  }

  // const onPrevStep = () => {
  //   setActiveStep((prev: number) => prev - 1);
  // }

  const { data: packetData, isLoading: isLoadingPacketData } = useGetOwnershipChangePacketById({
    ocpId: id as string,
    queryParams: {
      enabled: !!id,
    },
  });
    
  const { data: propertyList, isLoading: isPropertyLoading } =
    useGetPropertiesByCommunity({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: !!(communityData?.communityId && id),
      },
    });

  useEffect(() => {
    if (packetData) {
     
      dispatch(setOCPData({...packetData?.data}));
      const currStep = steps?.find((data: any) => data.status == packetData?.data?.status);
      setActiveStep(currStep?.id as any);
    }
  }, [packetData]);

  useEffect(() => {
    if (propertyList?.data && packetData) {
      const propertyData = propertyList?.data?.find((data: any) => data?.propertyId == propertyId);
      dispatch(setPendingBillData({
        [PROPERTY_ID]: packetData?.data?.propertyId?.toString(),
        [OWNER_ID]: propertyData?.[OWNER_ID]?.toString()
      }))
    }
  }, [propertyList, packetData])

  const {
    mutateAsync: addFeeDoc,
    isPending: isAddFeeDocPending,
  } = useAddOCPFeeDocMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });

  const {
    mutateAsync: addIntakeDoc,
    isPending: isAddIntakeDocPending,
  } = useAddOCPIntakeDocumentMutation({
    onError: (error?: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error?.response?.data?.message
          : "Something went wrong"
      );
    },
  });

  const uploadFeeDocs = async (data: any, ocpId: string) => {
    if (activeStep == 2) {
      const receipts = data?.feeSummary?.[RECEIPTS] || [];

      
      const docExist = proofDocs?.current?.filter(
        (file: any) => file?.[RECEIPT_PROOF] instanceof File && !file?.[RECEIPT_ID]
      ) || [];

      
      const uploadPromises = docExist.map((file: any) => {
        const formData = new FormData();
        formData.append("file", file?.[RECEIPT_PROOF]);

        const currReceipt = receipts?.find((val: any) => val?.[CHEQUE_NO] == file?.[CHEQUE_NO] || val?.[RECEIPT_ID] == file?.[RECEIPT_ID]);
  
        return addFeeDoc({
          ocpId: ocpId.toString(),
          receiptId: currReceipt?.[RECEIPT_ID],
          data: formData,
        });
      });

      try {
        // 1. Wait for all mutation promises to resolve
        const results: any = await Promise.all(uploadPromises);
        
        const allSuccessful = results.every((res: any) => res?.status === 201);
        
        if (allSuccessful) {
          queryClient.invalidateQueries({queryKey: ["ownership-change-packet", ocpId]});
          queryClient.invalidateQueries({ queryKey: ["fee-types"]});

          const _ocpData = {
            ...data, 
            [RECEIPTS]: data?.feeSummary?.[RECEIPTS]?.map((rec: any) => {
              // Find matching attachment by receiptId
              const attachment = results?.find((res: any) => res?.data?.data?.entityId == rec?.[RECEIPT_ID]);

              return {
                ...rec,
                ...(attachment ? { proofAttachments: [attachment?.data?.data] } : {})
              };
            })
          }
          
          dispatch(setOCPData(_ocpData));
          return results;
        }
      } catch (error) {
        console.error("One or more files failed to upload:", error);
        throw error;
      }
    }
  }

  const handleSuccessNavigation = () => {
    toast.success(
      `Ownership Change Packet ${isEdit ? "Updated" : "Created"} Successfully`
    );
  
    if (activeStep < 5) {
      onNextStep();
    } else {
      onCancel();
    }
  };
  
  const { mutate: addOwnershipChangePacket, isPending: isAddPending } =
    useAddOwnershipChangePacketMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
      },
      onSuccess: async ({ data }: any) => {
        try {
          const ocpId = data?.data?.[OCP_ID];
          dispatch(setOCPData({ ...ocpData, ...data?.data }));
                   
          if (activeStep == 1 && proofDocs.current && proofDocs.current instanceof File) {
            const formData = new FormData();
            formData.append("file", proofDocs.current);

            const result: any = await addIntakeDoc({
              ocpId,
              data: formData
            });

            if (result.status == 201) {
              const res = result?.data?.data;
              dispatch(setOCPData({ ...ocpData, [PROOF_FILE]: { 
                [ATTACHMENT_ID]: res?.[ATTACHMENT_ID],
                name: res?.originalFileName,
                size: res?.fileSize
              }}));
            }
          }

          await uploadFeeDocs(data?.data, ocpId);

          if (!(activeStep == 2 && isFeeDraft.current)) {
            handleSuccessNavigation();
          } else {
            toast.success(
              `Fee Saved as Draft`
            );
          }
        } catch (error) {
          console.error("Error in post-success operations:", error);
          toast.error("Something went wrong while processing attachments.", {
            className: "!bg-red-600 !text-white",
          });
        }
      },
    });

  const onSubmit = async (data: any) => {
    let payload = { ...data };
  
    delete payload[OWNER];
    if (activeStep == 2) {
      isFeeDraft.current = payload?.draft
      proofDocs.current = payload[RECEIPTS];
    }
    
    // --- Call Main API ---
    if (activeStep === 1) {
      proofDocs.current = payload[PROOF_FILE];
      addOwnershipChangePacket({ data: payload });
    } else {
      addOwnershipChangePacket({
        data: {
          ...payload,
          [PROPERTY_ID]: ocpData[PROPERTY_ID],
          [OCP_ID]: ocpData[OCP_ID],
        },
      });
    }
  };
  
  return {
    breadcrumbData,
    onStepperClick,
    activeStep,
    onCancel,
    onSubmit,
    isAddPending: isAddPending || isAddFeeDocPending || isAddIntakeDocPending,
    title,
    isLoadingPacketData: isLoadingPacketData || isPropertyLoading,
    isView,
    ocpData,
    isBillPending
  }
}

export default useAddEditOCP;